import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { clearPendingInvite, getPendingInvite } from '../utils/invite';
import { formatDate, ROLE_HELP, ROLE_LABELS, useOrgApi } from '../utils/orgApi';
import { ConstructionLoader } from '../components/ConstructionLoader';

interface Preview {
  email: string;
  role: string;
  status: string;
  org_name: string | null;
  expires_at: string | null;
  matches_me: boolean;
  already_member: boolean;
}

/** Plain-English versions of the accept endpoint's refusals (batch 7). */
const REFUSALS: Record<string, string> = {
  invitation_not_found: 'This invitation is no longer valid. It may have been revoked or already used. Ask your firm admin for a new link.',
  invitation_expired: 'This invitation has expired. Ask your firm admin for a new link.',
  invitation_email_mismatch: 'This invitation was sent to a different email address. Sign out and sign in with the invited address.',
  already_in_a_firm: 'You are already a member of another firm. Ask that firm\'s admin to remove you first, then open this link again.',
  last_admin: 'You are the only admin of your current firm. Make someone else an admin there first, then open this link again.',
  seat_limit_reached: 'This firm has no seats left. Ask the firm admin to free one up.',
};

const AcceptInvite: React.FC = () => {
  const api = useOrgApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userLoaded, refreshUser } = useUser();

  // Read once: the stored copy is cleared as soon as the page has shown the
  // invitation, so it never sends the user here again from the models page.
  const [token] = useState<string | null>(
    () => new URLSearchParams(location.search).get('token') || getPendingInvite()
  );
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState<string | null>(null);
  // A boolean, not the user object: refreshUser() after joining replaces the
  // object and must not re-run the preview.
  const userReady = userLoaded && Boolean(user);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setError('This page needs the full invitation link. Open the link you were sent again.');
        setLoading(false);
        return;
      }
      // Wait for /check_user. On a first login it is what creates the account
      // (and, for an invited address, joins the firm), so a preview request
      // sent before it answers is refused with "User not found".
      if (!userLoaded) return;
      if (!userReady) {
        setError('Could not load your account. Reload the page, or sign out and sign in again.');
        setLoading(false);
        return;
      }
      const r = await api<Preview>('GET', `/invitations/${encodeURIComponent(token)}`);
      if (cancelled) return;
      clearPendingInvite();
      if (r.ok) {
        if (r.data.already_member && r.data.status === 'accepted') {
          // Joined at sign-up (the address was invited before the first login).
          setJoined(r.data.org_name || 'the firm');
        } else {
          setPreview(r.data);
        }
      } else {
        setError(REFUSALS[r.code || ''] || r.error || 'Could not open this invitation.');
      }
      setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [api, token, userLoaded, userReady]);

  const goHome = () => {
    clearPendingInvite();
    navigate('/', { replace: true });
  };

  const accept = async () => {
    if (!token) return;
    setJoining(true);
    const r = await api<{ org_name: string }>('POST', `/invitations/${encodeURIComponent(token)}/accept`);
    setJoining(false);
    if (!r.ok) {
      setError(REFUSALS[r.code || ''] || r.error || 'Could not accept this invitation.');
      return;
    }
    clearPendingInvite();
    setJoined(r.data?.org_name || preview?.org_name || 'the firm');
    // The models page reloads the deal list when it mounts.
    await refreshUser();
  };

  const firm = preview?.org_name || 'this firm';

  let body: React.ReactNode;
  if (loading) {
    body = <ConstructionLoader />;
  } else if (joined) {
    body = (
      <>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>You joined {joined}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          The firm's deals are on your models page now, along with any deals you had before.
        </Typography>
        <Button variant="contained" onClick={goHome}>Go to the firm's deals</Button>
      </>
    );
  } else if (error && !preview) {
    body = (
      <>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Invitation</Typography>
        <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>{error}</Alert>
        <Button variant="outlined" onClick={goHome}>Go to your models</Button>
      </>
    );
  } else if (preview && preview.already_member) {
    body = (
      <>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>You are already in {firm}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Nothing else to do.</Typography>
        <Button variant="contained" onClick={goHome}>Go to the firm's deals</Button>
      </>
    );
  } else if (preview && !preview.matches_me) {
    body = (
      <>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>This invitation is for someone else</Typography>
        <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
          It was sent to <strong>{preview.email}</strong>, but you are signed in as <strong>{user?.email}</strong>.
          Sign out and sign in with the invited address to accept it.
        </Alert>
        <Button variant="outlined" onClick={goHome}>Go to your models</Button>
      </>
    );
  } else if (preview) {
    body = (
      <>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Join {firm} on UnderwritRE</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          You have been invited as <strong>{ROLE_LABELS[preview.role] || preview.role}</strong>. {ROLE_HELP[preview.role]}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Any deals you already have move into the firm, where every member can see them.
          This invitation expires {formatDate(preview.expires_at)}.
        </Typography>
        {error && <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={accept} disabled={joining}>
            {joining ? 'Joining...' : `Join ${firm}`}
          </Button>
          <Button onClick={goHome}>Not now</Button>
        </Box>
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, mt: { xs: 4, md: 8 } }}>
      <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 }, maxWidth: 560, width: '100%', textAlign: 'center' }}>
        {body}
      </Paper>
    </Box>
  );
};

export default AcceptInvite;
